import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./Sidebar.css";
import "font-awesome/css/font-awesome.min.css";
import { useAuth } from "../common/AuthContext"; // ✅ correct path

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const sidebarRef = useRef();
  const menuRef = useRef();
  const navigate = useNavigate();

  const { user, logout, getUserPlan } = useAuth();

  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const isLoggedIn = !!user; // ✅ safe check

  const openSidebar = () => setIsOpen(true);

  const closeSidebar = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const handleLogout = () => {
    logout();
    closeSidebar();
    navigate("/login");
  };

  const navigateTo = (path) => {
    navigate(path);
    closeSidebar();
  };

  const checkUserPlanAndNavigate = (path) => {
    const userPlan = getUserPlan();

    if (userPlan) {
      navigate(path);
    } else {
      navigate("/subscribe");
    }

    closeSidebar();
  };

  // ✅ Hook always runs (no conditional issue)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        closeSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return isLoggedIn ? (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`sidenav ${isOpen ? "open" : ""}`}
        ref={sidebarRef}
        onMouseLeave={() => !isTouchDevice && closeSidebar()}
      >
        {/* Logo */}
        <div className="sidebar-logo">
          <img
            src="/assets/images/noptionwave-logo-1.png"
            className="logo-image"
            alt="logo"
          />
        </div>

        {/* Dashboard */}
        <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/dashboard"); }}>
          <i className="fa fa-info-circle"></i> DASHBOARD
        </a>

        {/* NSE DATA */}
        <div
          className={`dropdown-wrapper ${activeDropdown === 2 ? "active" : ""}`}
          onMouseEnter={() => !isTouchDevice && setActiveDropdown(2)}
          onMouseLeave={() => !isTouchDevice && setActiveDropdown(null)}
          onClick={() => isTouchDevice && toggleDropdown(2)}
        >
          <button className="dropdown-btn">
            <i className="fa fa-bar-chart"></i> NSE DATA{" "}
            <i className="fa fa-caret-down"></i>
          </button>

          <AnimatePresence>
            {activeDropdown === 2 && (
              <motion.div className="dropdown-container">
                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/nse-option-chain"); }}>
                  <i className="fa fa-line-chart"></i> NSE OPTION CHAIN
                </a>

                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/fifth-strike-option-chain"); }}>
                  <i className="fa fa-line-chart"></i> 5TH STRIKE DATA
                </a>

                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/day-range-analysis"); }}>
                  <i className="fa fa-line-chart"></i> DAY RANGE ANALYSIS
                </a>

                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/oi-data-analysis"); }}>
                  <i className="fa fa-line-chart"></i> OI DATA ANALYSIS
                </a>

                {user?.admin && (
                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/option-chain-analysis"); }}>
                    <i className="fa fa-line-chart"></i> OPTION CHAIN ANALYSIS
                  </a>
                )}

                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/greeks"); }}>
                  <i className="fa fa-line-chart"></i> GREEKS
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* STOCK DATA */}
        <div
          className={`dropdown-wrapper ${activeDropdown === 3 ? "active" : ""}`}
          onMouseEnter={() => !isTouchDevice && setActiveDropdown(3)}
          onMouseLeave={() => !isTouchDevice && setActiveDropdown(null)}
          onClick={() => isTouchDevice && toggleDropdown(3)}
        >
          <button className="dropdown-btn">
            <i className="fa fa-bar-chart"></i> STOCK DATA{" "}
            <i className="fa fa-caret-down"></i>
          </button>

          <AnimatePresence>
            {activeDropdown === 3 && (
              <motion.div className="dropdown-container">
                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/stock-data-analysis"); }}>
                  <i className="fa fa-line-chart"></i> STOCK DATA ANALYSIS
                </a>

                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/stock-sector-analysis"); }}>
                  <i className="fa fa-line-chart"></i> STOCK SECTOR ANALYSIS
                </a>

                {user?.admin && (
                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/sectorial-indices"); }}>
                    <i className="fa fa-line-chart"></i> SECTORIAL INDICES
                  </a>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* CRYPTO */}
        <div
          className={`dropdown-wrapper ${activeDropdown === 1 ? "active" : ""}`}
          onMouseEnter={() => !isTouchDevice && setActiveDropdown(1)}
          onMouseLeave={() => !isTouchDevice && setActiveDropdown(null)}
          onClick={() => isTouchDevice && toggleDropdown(1)}
        >
          <button className="dropdown-btn">
            <i className="fa fa-bar-chart"></i> CRYPTO DATA{" "}
            <i className="fa fa-caret-down"></i>
          </button>

          <AnimatePresence>
            {activeDropdown === 1 && (
              <motion.div className="dropdown-container">
                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/crypto-option-chain"); }}>
                  <i className="fa fa-line-chart"></i> CRYPTO OPTION CHAIN
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {user?.admin && user?.superAdmin && (
          <a href="fyers-access-token" onClick={closeSidebar}>
            <i className="fa fa-key"></i> Fyers Access Token
          </a>
        )}
        {/* Other Links */}
        <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/subscribe"); }}>
          <i className="fa fa-rupee"></i> Subscribe
        </a>

        <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/profile"); }}>
          <i className="fa fa-user-circle"></i> Profile
        </a>

        {/* User */}
        <div className="user-profile">
          <div className="user-info">
            <i className="fa fa-user-circle fa-2x"></i>
            <div className="user-text">
              <strong>{user?.userName || "--"}</strong>
              <small>{user?.email || "--"}</small>
            </div>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            <i className="fa fa-sign-out"></i>
          </button>
        </div>
      </div>

      {/* Menu Button */}
      <button
        className="openbtn"
        ref={menuRef}
        onMouseEnter={() => !isTouchDevice && openSidebar()}
        onClick={() => isTouchDevice && openSidebar()}
      >
        ☰
      </button>
    </>
  ) : null;
};

export default Sidebar;